"use client";
import { format, formatISO, parseISO } from "date-fns";
import { isNil } from "lodash";
import { useTranslations } from "next-intl";
import React, { ChangeEvent, forwardRef, useEffect, useState } from "react";

import { Input, InputProps } from "@/components/ui/form_field";
import { logError } from "@/utils/core/errors";

interface DatetimeUtcProps extends Omit<InputProps, "onChange"> {
  defaultValue?: string;
  onChange?: (value: string | undefined) => void;
  onError?: (error: unknown) => void;
  withFormValidation?: boolean;
  className?: string;
  withTimezoneMessage?: boolean;
  /**
   * Render and accept the value in UTC rather than the viewer's local time.
   *
   * Needed wherever a native `step` constrains the value: the browser's step
   * grid is anchored in the input's own naive time, so converting to local makes
   * a UTC-aligned instant fail stepMismatch outside UTC, and shifts by an hour
   * across a daylight-saving boundary. Showing UTC directly makes the naive
   * value and the stored value the same clock, so the grid lines up.
   */
  utc?: boolean;
}

/**
 * Datetime input component which renders datetime in user's local timezone
 * but stores and accepts values in UTC format
 */
const DatetimeUtc = forwardRef<HTMLInputElement, DatetimeUtcProps>(
  (
    {
      defaultValue,
      onChange,
      onError,
      withFormValidation,
      className,
      withTimezoneMessage = true,
      utc = false,
      ...props
    },
    ref
  ) => {
    const t = useTranslations();
    const formatStr =
      props.type === "date" ? "yyyy-MM-dd" : "yyyy-MM-dd'T'HH:mm";
    // Seed from defaultValue so the very first paint already shows it. That is
    // what lets a caller remount (via `key`) to apply a programmatic bound
    // change without making the input controlled.
    const [localValue, setLocalValue] = useState<string>(() =>
      toInputString(defaultValue, formatStr, utc)
    );

    useEffect(() => {
      if (!isNil(defaultValue)) {
        const displayed = toInputString(defaultValue, formatStr, utc);
        if (displayed) {
          setLocalValue(displayed);
        }
      }
    }, [defaultValue, formatStr, utc]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const localDateString = event.target.value;
      setLocalValue(localDateString);

      try {
        // Convert local time to UTC for storage
        if (onChange) {
          if (!localDateString.trim()) {
            onChange(undefined);
            return;
          }

          // In UTC mode the typed value already IS UTC, so append the zone
          // rather than letting Date parse it as local.
          const parsed = utc
            ? new Date(`${localDateString}Z`)
            : new Date(localDateString);
          const utcDateString = formatISO(parsed, {
            representation: "complete",
          });

          onChange(utcDateString);
        }
      } catch (e) {
        if (withFormValidation) {
          onChange?.(undefined);
          return;
        }

        logError(e, { payload: localDateString });
        onError && onError(e);
      }
    };

    const inputElement = (
      <Input
        ref={ref}
        type="datetime-local"
        // Uncontrolled on purpose: a controlled value round-trips every
        // keystroke through the parent, which fights the browser's
        // segment-by-segment date editing. The state above is seeded lazily
        // from defaultValue, so a caller that needs to change the bound
        // programmatically can remount via `key` and get the new value on the
        // first paint.
        defaultValue={localValue}
        className={className}
        onChange={handleChange}
        onBlur={handleChange}
        {...props}
      />
    );

    return withTimezoneMessage ? (
      <div className="flex flex-col gap-1">
        {inputElement}
        <span className="text-center text-xs font-normal normal-case italic">
          {t("dateInputDetails", {
            timezone: utc ? "UTC" : getTimezoneOffsetLabel(),
          })}
        </span>
      </div>
    ) : (
      inputElement
    );
  }
);
DatetimeUtc.displayName = "DatetimeUtc";

/**
 * Render a stored UTC value as the string the input shows — in the viewer's
 * local time by default, or in UTC when the caller asks for it.
 */
const toInputString = (
  value: string | undefined,
  formatStr: string,
  utc: boolean
): string => {
  if (isNil(value)) return "";
  const parsed = parseISO(value);
  if (isNaN(parsed.getTime())) return "";
  if (!utc) return format(parsed, formatStr);
  // Shift by the offset so date-fns' local formatting prints UTC wall time.
  const shifted = new Date(
    parsed.getTime() + parsed.getTimezoneOffset() * 60000
  );
  return format(shifted, formatStr);
};

const getTimezoneOffsetLabel = (): string => {
  const timezoneOffset = new Date().getTimezoneOffset() / -60;
  return `UTC${timezoneOffset >= 0 ? "+" : ""}${timezoneOffset}`;
};

export default DatetimeUtc;
